import React, {useImperativeHandle, useRef, useState} from 'react';
import classNames from 'classnames';
import {useIntl} from 'react-intl';

import {Tooltip, TooltipTextPrimary} from '@time-webkit/all/atoms/tooltip';
import {OpenEye} from '@time-webkit/all/icons/open-eye';
import {ClosedEye} from '@time-webkit/all/icons/closed-eye';
import {FormError} from '@time-webkit/all/atoms/form-error';
import {useTooltipTrigger} from '@react-aria/tooltip';
import {useTooltipTriggerState} from '@react-stately/tooltip';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {useFocusWithin} from '@react-aria/interactions';

import {Input} from '../../input';
import {INPUT_SIZE} from '../../text-input';

import styles from './styles.module.css';

type Props = {
    className?: string;
    maxCounter?: number;
    errorMessage?: string;
} & React.ComponentProps<typeof Input>;

export const FormInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
    const {formatMessage} = useIntl();

    const [inputType, setInputType] = useState(props.type || 'text');
    const [isFocused, setIsFocused] = useState(false);
    const {focusWithinProps} = useFocusWithin({onFocusWithinChange: setIsFocused});
    const state = useTooltipTriggerState({delay: 50});

    const {className, errorMessage, maxCounter, ...inputProps} = props;
    const size = inputProps?.size ?? INPUT_SIZE.LARGE;

    const targetRef = useRef(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const {triggerProps, tooltipProps} = useTooltipTrigger({}, state, targetRef);
    const bodyL = useTypography({size: TypographySize.BodyL});

    const toggleType = (e: React.MouseEvent) => {
        e.stopPropagation();
        setInputType(inputType === 'password' ? 'text' : 'password');

        // requestAnimationFrame позволяет отобразить каретку в конце строки после onBlur и onFocus, которые порождаются изменением типа инпута
        requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(inputRef.current.value.length, inputRef.current.value.length);
        });
    };

    useImperativeHandle(ref, () => {
        return inputRef.current as HTMLInputElement;
    });

    const hasFocus = Boolean(inputProps.value || isFocused);
    const EyeIcon = inputType === 'password' ? OpenEye : ClosedEye;
    const showEye = hasFocus && inputProps.type === 'password';

    const getRightIcon = () => {
        const inputValue = inputProps.value ?? '';
        const showMaxCounter = maxCounter && inputValue && maxCounter < inputValue.length;

        return (
            <div className={styles.icons}>
                {showMaxCounter && (
                    <div className={classNames(styles.maxCounter, bodyL)}>−{inputValue.length - maxCounter}</div>
                )}
                {showEye && (
                    <EyeIcon
                        className={styles.eye}
                        width={20}
                        height={20}
                        ref={targetRef}
                        {...triggerProps}
                        onClick={toggleType}
                    />
                )}
            </div>
        );
    };

    return (
        <div
            className={className}
            {...focusWithinProps}
        >
            <Input
                {...inputProps}
                type={inputType}
                ref={inputRef}
                size={size}
                iconRight={getRightIcon()}
            />
            {state.isOpen && (
                <Tooltip
                    {...tooltipProps}
                    state={state}
                    targetRef={targetRef}
                >
                    <TooltipTextPrimary>
                        {inputType === 'password' ? formatMessage({id: 'signup.show_password', defaultMessage: 'Show password'}) : formatMessage({id: 'signup.hide_password', defaultMessage: 'Hide password'})}
                    </TooltipTextPrimary>
                </Tooltip>
            )}
            <FormError
                show={Boolean(errorMessage)}
                message={errorMessage}
            />
        </div>
    );
});
